import type { FeatureType } from "schemas/agent-billing";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Badge } from "@replohq/lattice/components/badge/Badge";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  RadioGroup,
  RadioGroupItem,
} from "@replohq/lattice/components/radio-group/RadioGroup";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { cn } from "@replohq/lattice/utils/cn";
import { MessageCircle, Sparkles, Zap } from "lucide-react";
import { formatNumber, formatPrice } from "replo-utils/lib/format";

import { usePurchaseCredits } from "../hooks/usePurchaseCredits";
import { useRateCards } from "../hooks/useRateCards";

function getCreditPackageDetails({
  baseQuantity,
  basePrice,
  multiplier,
}: {
  baseQuantity: number;
  basePrice: number;
  multiplier: number;
}) {
  return {
    quantity: baseQuantity * multiplier,
    price: basePrice * multiplier,
  };
}

type CreditProductOption = {
  multiplier: number;
  tagline: string;
  icon: React.ReactNode;
  isRecommended?: boolean;
};

const CREDIT_PRODUCT_OPTIONS: CreditProductOption[] = [
  {
    multiplier: 1,
    tagline: "For occasional use",
    icon: <MessageCircle className="size-4 text-muted" />,
  },
  {
    multiplier: 2,
    tagline: "Best value for regular use",
    icon: <Zap size={15} className="text-muted" />,
    isRecommended: true,
  },
  {
    multiplier: 4,
    tagline: "For heavy builders",
    icon: <Sparkles className="size-4 text-muted" />,
  },
];

export const AgentBillingAddCreditsModal: React.FC<{
  featureType: FeatureType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ featureType, open, onOpenChange }) => {
  const { purchase, isLoading: isPurchasing } = usePurchaseCredits({
    featureType,
  });
  const { rateCards } = useRateCards();
  const [selectedOptionIndex, setSelectedOptionIndex] = React.useState(1);

  const creditProduct = rateCards
    ?.flatMap((rateCard) => rateCard.addOnProducts)
    .find((addOnProduct) => addOnProduct.featureType === featureType);

  const defaultPrice = creditProduct?.prices.find(
    (priceEntry) => priceEntry.isDefault,
  );
  if (!creditProduct || creditProduct.units === null || !defaultPrice) {
    return null;
  }

  const baseQuantity = creditProduct.units;
  const basePrice = defaultPrice.amount;
  const currency = defaultPrice.currency;

  const selectedOption =
    CREDIT_PRODUCT_OPTIONS[selectedOptionIndex] ?? CREDIT_PRODUCT_OPTIONS[0]!;
  const selected = getCreditPackageDetails({
    baseQuantity,
    basePrice,
    multiplier: selectedOption.multiplier,
  });

  const handleConfirm = async () => {
    await purchase({ quantity: selected.quantity });
    onOpenChange(false);
  };

  return (
    <SplitModal
      open={open}
      onOpenChange={onOpenChange}
      media={
        <img
          src="/images/billing/chat-topup-graphic.png"
          alt="Add credits illustration"
          className="h-full w-full object-cover"
        />
      }
    >
      <div className="flex flex-1 flex-col justify-between gap-6">
        <div className="flex flex-col gap-1">
          <div className="text-[32px] font-editorial leading-tight">
            Add <span className="italic">credits</span>
          </div>
          <p className="text-sm text-muted">Purchased credits never expire.</p>
        </div>

        <RadioGroup
          value={String(selectedOptionIndex)}
          onValueChange={(value) => setSelectedOptionIndex(Number(value))}
        >
          {CREDIT_PRODUCT_OPTIONS.map((option, index) => {
            const creditPackage = getCreditPackageDetails({
              baseQuantity,
              basePrice,
              multiplier: option.multiplier,
            });
            const isSelected = selectedOptionIndex === index;
            return (
              <div
                key={option.multiplier}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedOptionIndex(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedOptionIndex(index);
                  }
                }}
                className={cn(
                  "flex h-[68px] cursor-pointer items-center gap-3 rounded-xl border-0.5 p-4 transition-colors",
                  isSelected
                    ? "border-default bg-sheet"
                    : "border-border hover:bg-sheet",
                )}
              >
                <RadioGroupItem value={String(index)} className="hidden" />
                <Badge
                  size="xl"
                  render={<div />}
                  className={cn(
                    isSelected &&
                      "bg-pink-soft border-pink-border [&>svg]:text-pink",
                  )}
                >
                  {option.icon}
                </Badge>
                <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {formatNumber(creditPackage.quantity)} credits
                    </span>
                    {option.isRecommended && (
                      <Tag size="sm" variant="neutral">
                        Popular
                      </Tag>
                    )}
                  </div>
                  <span className="text-xs text-muted">{option.tagline}</span>
                </div>
                <span className="text-base font-semibold shrink-0">
                  {formatPrice({ amount: creditPackage.price, currency })}
                </span>
              </div>
            );
          })}
        </RadioGroup>

        <Button
          variant="primary"
          size="base"
          className="w-full"
          onClick={() => void handleConfirm()}
          loading={isPurchasing}
        >
          Add {formatNumber(selected.quantity)} credits (
          {formatPrice({ amount: selected.price, currency })})
        </Button>
      </div>
    </SplitModal>
  );
};
